import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
//import ImageWithBasePath from "../../core/img/imagewithbasebath";
//import { setToogleHeader } from "../../core/redux/action";
import Table from "../../core/pagination/datatable";
import { Modal, Button, Tag, notification, Input, DatePicker, Space } from "antd";
import dayjs from 'dayjs';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';

dayjs.extend(isSameOrBefore);
dayjs.extend(isSameOrAfter);
import {
  fetchContracts,
  addContract,
  editContract,
  removeContract,
  openModal,
  closeModal,
  openDeleteModal,
  closeDeleteModal
} from "../../core/redux/contract.slice";
import { fetchCustomerSummaries } from "../../core/redux/customer.slice";
import ContractFormModal from "../../core/modals/peoples/ContractFormModalNew";
import { canSee } from "../../core/utils/permissions";
import CommonFooter from "../../core/common/footer/commonFooter";
import LoadingState from "../../core/common/LoadingState";
import StickyBar from "../../core/common/StickyBar";
import { useTranslation } from "react-i18next";

const CustomerContract = () => {
  const dispatch = useDispatch();
  const { t } = useTranslation();

  const { contracts, loading, showModal, showDeleteModal } = useSelector((state) => state.contract);
  const { customers } = useSelector((state) => state.customer);
  //const data = useSelector((state) => state.rootReducer.toggle_header);
  const { user } = useSelector((state) => state.auth);
  const canDelete = canSee(user?.roles, 'ADMIN') || canSee(user?.roles, 'SUPERADMIN');
  const [isEditMode, setIsEditMode] = useState(false);
  const [isReadOnly, setIsReadOnly] = useState(false);
  const [selectedContract, setSelectedContract] = useState(null);
  const [contractToDelete, setContractToDelete] = useState(null);

  // Filter states
  const [searchClientName, setSearchClientName] = useState("");
  const [searchContractNumber, setSearchContractNumber] = useState("");
  const [dateRange, setDateRange] = useState([null, null]);

  useEffect(() => {
    dispatch(fetchContracts());
    dispatch(fetchCustomerSummaries(false));
  }, [dispatch]);

  const handleAdd = () => {
    setIsEditMode(false);
    setIsReadOnly(false);
    setSelectedContract(null);
    dispatch(openModal());
  };

  const handleEdit = (contract) => {
    setIsEditMode(true);
    setIsReadOnly(false);
    setSelectedContract(contract);
    dispatch(openModal());
  };

  // const handleView = (contract) => {
  //   setIsEditMode(false);
  //   setIsReadOnly(true);
  //   setSelectedContract(contract);
  //   dispatch(openModal());
  // };

  const handleCancel = () => {
    dispatch(closeModal());
  };

  const deleteContract = () => {
    dispatch(removeContract(contractToDelete))
      .unwrap()
      .then(() => {
        notification.success({
          message: t("contracts.messages.success"),
          description: t("contracts.messages.deleted"),
          placement: 'topRight',
        });
      })
      .catch((error) => {
        notification.error({
          message: t("contracts.messages.error"),
          description: error || t("contracts.messages.deleteError"),
          placement: 'topRight',
        });
      });
  };

  const handleFormSubmit = (values, cb) => {
    if (isReadOnly) {
      handleCancel();
      return;
    }
    if (isEditMode) {
      dispatch(editContract({ id: selectedContract.contractId, contract: values }))
        .unwrap()
        .then(() => {
          notification.success({
            message: t("contracts.messages.error"),
            description: t("contracts.messages.updated"),
            placement: 'topRight',
          });
          handleCancel();
          cb();
        })
        .catch((error) => {
          notification.error({
            message: t("contracts.messages.error"),
            description: error || t("contracts.messages.modificationError"),
            placement: 'topRight',
          });
          console.error('Error updating contract:', error);
        });
    }
    else {
      dispatch(addContract(values))
        .unwrap()
        .then(() => {
          notification.success({
            message: t("contracts.messages.success"),
            description: t("contracts.messages.created"),
            placement: 'topRight',
          });
          handleCancel();
          cb();
        })
        .catch((error) => {
          notification.error({
            message: t("contracts.messages.error"),
            description: error || t("contracts.messages.creationError"),
            placement: 'topRight',
          });
          console.error('Error creating contract:', error);
        });
    }
  };

  const getCustomerName = (customerId) => {
    const customer = customers.find(c => c.customerId === customerId);
    return customer ? customer.customerName : t("contracts.fields.unassigned");
  };

  // Filter function
  const applyFilters = (contractList) => {
    return contractList.filter(contract => {
      // Client name filter
      if (searchClientName) {
        const customerName = contract.customerName || getCustomerName(contract.customerId);
        if (!customerName.toLowerCase().includes(searchClientName.toLowerCase())) {
          return false;
        }
      }

      // Contract number filter
      if (searchContractNumber) {
        if (!contract.contractNumber || !contract.contractNumber.toLowerCase().includes(searchContractNumber.toLowerCase())) {
          return false;
        }
      }

      // Date range filter - check if contract is active during the selected range
      if (dateRange && dateRange[0] && dateRange[1]) {
        const rangeStart = dayjs(dateRange[0]).startOf('day');
        const rangeEnd = dayjs(dateRange[1]).endOf('day');
        const contractStart = dayjs(contract.contractStart);
        const contractEnd = dayjs(contract.contractEnd);

        // Contract is active in the range if:
        // contractStart <= rangeEnd AND contractEnd >= rangeStart
        const isActiveInRange = contractStart.isSameOrBefore(rangeEnd) && contractEnd.isSameOrAfter(rangeStart);

        if (!isActiveInRange) {
          return false;
        }
      }

      return true;
    });
  };

  const filteredContracts = applyFilters(contracts);
  const columns = [
    {
      title: t("contracts.columns.number"),
      dataIndex: "contractNumber",
      render: (text, record) => (
        <Link to="#" onClick={() => handleEdit(record)}>{text}</Link>
      ),

      sorter: (a, b) => String(a.contractNumber).localeCompare(String(b.contractNumber)),
    },
    {
      title: t("contracts.columns.customer"),
      dataIndex: "customerId",
      render: (customerId) => (
        <span>
          {customerId ? getCustomerName(customerId) : <span style={{ color: "#888" }}>Non assigné</span>}
        </span>
      ),
      sorter: (a, b) => getCustomerName(a.customerId).localeCompare(getCustomerName(b.customerId)),
    },
    {
      title: t("contracts.columns.startDate"),
      dataIndex: "contractStart",
      render: (date) => date ? new Date(date).toLocaleDateString('fr-FR') : '-',
      sorter: (a, b) => new Date(a.contractStart) - new Date(b.contractStart),
    },
    {
      title: t("contracts.columns.endDate"),
      dataIndex: "contractEnd",
      render: (date) => date ? new Date(date).toLocaleDateString('fr-FR') : '-',
      sorter: (a, b) => new Date(a.contractEnd) - new Date(b.contractEnd),
    },
    {
      title: t("contracts.columns.products"),
      dataIndex: "contractLinesDto",
      render: (_, record) => (
        <div style={{ textAlign: "right" }}>
          {record.contractLinesDto.length}
        </div>
      ),
      sorter: (a, b) => Number(a.contractLinesDto.length) - Number(b.contractLinesDto.length),
    },

    {
      title: t("contracts.columns.status"),
      dataIndex: "contractIsActive",
      render: (isActive, record) => {
        const today = new Date();
        const endDate = record.contractEnd ? new Date(record.contractEnd) : null;
        const isExpired = endDate && endDate < today; // date de fin passée

        const finalIsActive = isActive && !isExpired;

        const label = isExpired
          ? t("contracts.status.expired")
          : finalIsActive
            ? t("contracts.status.active")
            : t("contracts.status.inactive");

        return (
          <Tag color={finalIsActive ? 'green' : 'red'}>
            {label}
          </Tag>
        );
      },
      sorter: (a, b) => {
        const today = new Date();

        const endA = a.contractEnd ? new Date(a.contractEnd) : null;
        const endB = b.contractEnd ? new Date(b.contractEnd) : null;

        const expiredA = endA && endA < today;
        const expiredB = endB && endB < today;

        const activeA = a.contractIsActive && !expiredA;
        const activeB = b.contractIsActive && !expiredB;

        if (activeA === activeB) return 0;
        return activeA ? 1 : -1;
      },
    },
    {
      title: t("contracts.columns.actions"),
      dataIndex: "actions",
      key: "actions",
      render: (_, record) => (
        <div className="action-table-data">
          <div className="edit-delete-action">
            {/* <Link className="me-2 p-2" to="#" onClick={() => handleView(record)}>
              <i data-feather="eye" className="feather feather-eye action-eye"></i>
            </Link> */}
            <Link className="me-2 p-2" to="#" onClick={() => handleEdit(record)}>
              <i data-feather="edit" className="feather-edit"></i>
            </Link>
            {canDelete && (
              <Link className="confirm-text p-2" to="#" onClick={() => {
                setContractToDelete(record.contractId);
                dispatch(openDeleteModal());
              }}>
                <i data-feather="trash-2" className="feather-trash-2"></i>
              </Link>
            )}
          </div>
        </div>
      ),
    },
  ];

  return (
    <div>
      <div className="page-wrapper">
        <div className="content">

          <StickyBar
            title={t("contracts.title")}
            count={filteredContracts.length}

            actions={
              <Space size="middle">
                <Button className="btn-customized-primary" >
                  <Link to="#" onClick={handleAdd}>
                    <i className='ti ti-circle-plus me-1'></i>
                    {t("contracts.actions.add")}
                  </Link></Button>
              </Space>} />

          <div className="card table-list-card">
            <div className="card-header d-flex align-items-center justify-content-between flex-wrap row-gap-3">
              <div className="search-set d-flex align-items-center gap-3">
                <div className="form-group">
                  <Input
                    placeholder={t("contracts.filters.customer")}
                    style={{ width: 200 }}
                    value={searchClientName}
                    onChange={(e) => setSearchClientName(e.target.value)}
                    allowClear
                  />
                </div>
                <div className="form-group">
                  <Input
                    placeholder={t("contracts.filters.contractNumber")}
                    style={{ width: 200 }}
                    value={searchContractNumber}
                    onChange={(e) => setSearchContractNumber(e.target.value)}
                    allowClear
                  />
                </div>
                <div className="form-group">
                  <DatePicker.RangePicker
                    format="DD/MM/YYYY"
                    placeholder={[t("contracts.filters.startDate"),
                    t("contracts.filters.endDate")]}
                    value={dateRange}
                    onChange={(dates) => setDateRange(dates)}
                    style={{ width: 300 }}
                  />
                </div>
              </div>
            </div>
            <div className="card-body">
              {loading ? (
                <LoadingState type="section" message={t("common.loadingData")} />
              ) : (
                <div className="table-responsive">
                  <Table
                    columns={columns}
                    dataSource={filteredContracts}
                    //  loading={loading}
                    rowKey="contractId"
                  />
                </div>
              )}
            </div>
          </div>
        </div>
        <CommonFooter />
      </div>
      <ContractFormModal
        open={showModal}
        onCancel={handleCancel}
        onSubmit={handleFormSubmit}
        isEdit={isEditMode}
        initialData={selectedContract}
        readOnly={isReadOnly}
      />
      <Modal onCancel={() => dispatch(closeDeleteModal())} open={showDeleteModal} footer="">
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content">
            <div className="page-wrapper-new p-0">
              <div className="content p-5 px-3 text-center">
                <span className="rounded-circle d-inline-flex p-2 bg-danger-transparent mb-2">
                  <i className="ti ti-trash fs-24 text-danger" />
                </span>
                <h4 className="fs-20 text-gray-9 fw-bold mb-2 mt-1">
                 {t("contracts.delete.title")}
                </h4>
                <p className="text-gray-6 mb-0 fs-16">
                {t("contracts.delete.confirm")}
                </p>
                <div className="modal-footer-btn mt-3 d-flex justify-content-center">
                  <Button
                    size="large"
                    type="button"
                    className="btn me-2 btn-submit fs-13 fw-medium p-2 px-3 shadow-none"
                    onClick={() => dispatch(closeDeleteModal())}
                  >
                      {t("common.cancel")}
                  </Button>
                  <Button
                    size="large"
                    type="button"
                    loading={loading}
                    onClick={deleteContract}
                    className="btn btn-submit fs-13 fw-medium p-2 px-3"
                  >
                    {t("common.confirmDelete")}
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default CustomerContract;